import { LazyQueryResultTuple } from "@apollo/client"
import { Box } from "@chakra-ui/react"
import React, { MutableRefObject, useEffect, useRef } from "react"
import { useDebounce, useIntersection } from "react-use"

type LazyQueryReturn = LazyQueryResultTuple<any, any>
type UseIntersectionProps = Parameters<typeof useIntersection>[1]
type Intersection = ReturnType<typeof useIntersection>
type IntersectionRef = MutableRefObject<any>

type UseIntersectionLazyLoadProps = {
  lazyQuery: LazyQueryReturn
  intersection?: UseIntersectionProps
  // Optionally debounce the lazy load after interesction occurs.
  debounce?: number
}

type UseIntersectionLazyLoadReturn = [IntersectionRef, LazyQueryReturn]

export function useIntersectionLazyLoad({
  lazyQuery,
  intersection: intersectionProps,
  debounce
}: UseIntersectionLazyLoadProps): UseIntersectionLazyLoadReturn {
  const intersectionRef = useRef(null)
  const intersection = useIntersection(intersectionRef, {
    // Use an expanding root margin both top and bottom (thought bottom likely will suffice)
    // to lazy load the query before the component enters visibility (rather than loading
    // just when component becomes visible).
    rootMargin: "200px 0px 200px 0px",
    threshold: 1,
    ...intersectionProps
  })

  // Handle debouncing, debouncing the intersection to a debounce state.
  const [debouncedIntersection, setDebouncedIntersection] =
    React.useState<Intersection>()

  const [, cancel] = useDebounce(
    () => {
      if (debounce) setDebouncedIntersection(intersection)
    },
    debounce,
    [intersection]
  )

  const [load, { data, loading }] = lazyQuery

  // Prefer debouncedIntersection when debouncing.
  const intersectionValue = debounce ? debouncedIntersection : intersection

  // Lazy load query once having interescted.
  useEffect(() => {
    if (intersectionValue?.isIntersecting && !data && !loading) {
      load()
    }
  }, [intersectionValue])

  // Cleanup debounce fn.
  useEffect(() => {
    return () => cancel()
  }, [])

  return [intersectionRef, lazyQuery]
}

export type IntersectionLazyLoadProps = {
  children: React.FC<LazyQueryReturn>
  fallback?: React.FC<LazyQueryReturn>
} & UseIntersectionLazyLoadProps

// Simple component that wraps children to conveniently handle intersection ref.
function IntersectionLazyLoad({
  children,
  fallback,
  ...intersectionLazyLoadProps
}: IntersectionLazyLoadProps) {
  const [intersectionRef, lazyQuery] = useIntersectionLazyLoad(
    intersectionLazyLoadProps
  )
  const [_load, { data }] = lazyQuery

  return (
    <Box ref={intersectionRef}>
      {data ? children(lazyQuery) : fallback ? fallback(lazyQuery) : null}
    </Box>
  )
}

export default IntersectionLazyLoad
